import { useState, useEffect, useRef } from "react";
import { isWeakPin, WEAK_PIN_MESSAGE } from "@shared/pinPolicy";
import { useParams, useLocation } from "wouter";
import { Phone, Loader2, Lock, KeyRound, ArrowLeft, UserPlus, CheckCircle, MessageSquare, LockKeyhole, Unlock, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { PinSetupCodeStep, PIN_CODE_ERROR_CODES, parseApiError } from "@/components/pin-setup-code-step";
import { chosenChildAccountId, lookupCustomer, type LookupAccountOption } from "@/lib/customerLookup";
import { AccountPickerStep } from "@/components/account-picker-step";

const KIOSK_SESSION_KEY = 'freshorder_kiosk_session';

interface KioskSession {
  sessionToken: string;
  expiresAt: string;
  orgSlug: string;
}

type KioskStep = "phone" | "pick-account" | "confirm-identity" | "pin" | "verify-code" | "set-pin" | "create-account" | "forgot-pin" | "reset-sent";

interface AccountInfo {
  id: number;
  name: string;
  customUrl: string;
  hasPin: boolean;
  requiresPinSetup: boolean;
  hasEmail: boolean;
  hasPhone: boolean;
  isGroupParent?: boolean;
  pinTarget?: { type: 'account' | 'contact'; id: number };
}

export default function KioskEntry() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Admin kiosk session state
  const [kioskMode, setKioskMode] = useState<"checking" | "locked" | "unlocked" | "unavailable">("checking");
  const [adminPin, setAdminPin] = useState("");
  const [kioskConfigured, setKioskConfigured] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(false);

  // Customer flow state
  const [step, setStep] = useState<KioskStep>("phone");
  const [phone, setPhone] = useState("");
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [newAccountName, setNewAccountName] = useState("");
  const [contactId, setContactId] = useState<number | null>(null);
  // A phone on more than one account: the options, and the one picked.
  const [accountOptions, setAccountOptions] = useState<LookupAccountOption[]>([]);
  const [chosenOption, setChosenOption] = useState<LookupAccountOption | null>(null);
  // DS-1: the one-time code that proves this customer holds the phone on file.
  const [setupCode, setSetupCode] = useState("");
  const [codeStepError, setCodeStepError] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState("your supplier");

  // Idle timeout - auto-reload after 2 minutes of inactivity
  const IDLE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      // Only reload if kiosk is unlocked and not on phone entry step
      if (kioskMode === "unlocked" && step !== "phone") {
        resetToPhone();
      }
    }, IDLE_TIMEOUT_MS);
  };

  // Set up idle detection listeners
  useEffect(() => {
    if (kioskMode !== "unlocked") return;

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    
    const handleActivity = () => {
      resetIdleTimer();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the idle timer
    resetIdleTimer();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [kioskMode, step]);

  // Check kiosk session on mount - including URL token
  useEffect(() => {
    checkKioskSession();
  }, [orgSlug]);

  // Swap PWA manifest to org-specific kiosk manifest for iOS home screen support
  // This ensures "Add to Home Screen" uses the kiosk URL instead of root URL
  useEffect(() => {
    if (!orgSlug) return;
    
    const kioskManifestUrl = `/org/${orgSlug}/kiosk-manifest.json`;
    let originalManifestHref: string | null = null;
    
    // Check if manifest is already pointing to kiosk manifest
    const existingManifest = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (existingManifest?.href?.includes('kiosk-manifest')) {
      return; // Already using kiosk manifest
    }
    
    // Store original manifest href for restoration
    if (existingManifest) {
      originalManifestHref = existingManifest.getAttribute('href');
      existingManifest.remove();
    }
    
    // Add org-specific kiosk manifest
    const kioskManifest = document.createElement('link');
    kioskManifest.rel = 'manifest';
    kioskManifest.href = kioskManifestUrl;
    document.head.appendChild(kioskManifest);
    
    // Also update the apple-mobile-web-app-title to org name if available
    const updateAppleTitle = async () => {
      try {
        const res = await fetch(`/org/${orgSlug}/api/organization`);
        if (res.ok) {
          const org = await res.json();
          if (org.name) setVendorName(org.name);
          const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
          if (appleTitle && org.name) {
            appleTitle.setAttribute('content', org.name);
          }
        }
      } catch (e) {
        // Ignore errors, just use default title
      }
    };
    updateAppleTitle();
    
    return () => {
      // Restore original manifest when leaving kiosk page
      const kioskManifestToRemove = document.querySelector('link[rel="manifest"][href*="kiosk-manifest"]');
      if (kioskManifestToRemove) {
        kioskManifestToRemove.remove();
      }
      // Only restore if we had an original manifest
      if (originalManifestHref) {
        const restoredManifest = document.createElement('link');
        restoredManifest.rel = 'manifest';
        restoredManifest.href = originalManifestHref;
        document.head.appendChild(restoredManifest);
      }
    };
  }, [orgSlug]);

  // Check for session token in URL query params (from admin Launch Kiosk button)
  const getUrlSessionToken = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get('session');
  };

  // Whether this device holds a kiosk session the server still accepts.
  const storedSessionIsValid = async (): Promise<boolean> => {
    try {
      const session: KioskSession = JSON.parse(localStorage.getItem(KIOSK_SESSION_KEY) || "null");
      if (!session || session.orgSlug !== orgSlug || new Date(session.expiresAt) < new Date()) return false;
      const validateRes = await fetch(`/org/${orgSlug}/api/kiosk/session`, {
        headers: { 'x-kiosk-session': session.sessionToken },
        credentials: 'include'
      });
      return validateRes.ok;
    } catch {
      return false;
    }
  };

  const checkKioskSession = async () => {
    if (!orgSlug) return;

    try {
      // Check for session token in URL (from admin Launch Kiosk button)
      const urlToken = getUrlSessionToken();
      if (urlToken) {
        // Validate URL token with server
        const validateRes = await fetch(`/org/${orgSlug}/api/kiosk/session`, {
          headers: {
            'x-kiosk-session': urlToken
          },
          credentials: 'include'
        });

        if (validateRes.ok) {
          const data = await validateRes.json();
          // Store session in localStorage for persistence (backup)
          const session: KioskSession = {
            sessionToken: urlToken,
            expiresAt: data.expiresAt,
            orgSlug
          };
          localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
          
          // IMPORTANT: Keep the session token in URL for iOS PWA home screen support
          // iOS doesn't share localStorage between Safari and PWA standalone mode
          // The URL with token is what gets saved when user adds to home screen
          
          setKioskConfigured(true);
          setKioskMode("unlocked");
          return;
        }
      }

      // First check if kiosk is configured for this org
      const statusRes = await fetch(`/org/${orgSlug}/api/kiosk/status`, {
        credentials: 'include'
      });
      
      if (!statusRes.ok) {
        // Kiosk not configured, allow access without PIN
        setKioskConfigured(false);
        setKioskMode("unlocked");
        return;
      }

      const statusData = await statusRes.json();

      // Kiosk mode is a paid add-on and is off for this vendor. A kiosk unlocked
      // before it was switched off keeps working until its session ends; nothing
      // else gets a kiosk, with or without a PIN on file.
      if (statusData.enabled === false) {
        if (await storedSessionIsValid()) {
          setKioskConfigured(true);
          setKioskMode("unlocked");
        } else {
          localStorage.removeItem(KIOSK_SESSION_KEY);
          setKioskMode("unavailable");
        }
        return;
      }

      if (!statusData.isConfigured) {
        setKioskConfigured(false);
        setKioskMode("unlocked");
        return;
      }

      setKioskConfigured(true);

      // Check for existing session in localStorage
      const storedSession = localStorage.getItem(KIOSK_SESSION_KEY);
      if (storedSession) {
        try {
          const session: KioskSession = JSON.parse(storedSession);
          
          // Verify session belongs to this org
          if (session.orgSlug !== orgSlug) {
            localStorage.removeItem(KIOSK_SESSION_KEY);
            setKioskMode("locked");
            return;
          }

          // Check if expired locally
          if (new Date(session.expiresAt) < new Date()) {
            localStorage.removeItem(KIOSK_SESSION_KEY);
            setKioskMode("locked");
            return;
          }

          // Validate session with server
          const validateRes = await fetch(`/org/${orgSlug}/api/kiosk/session`, {
            headers: {
              'x-kiosk-session': session.sessionToken
            },
            credentials: 'include'
          });

          if (validateRes.ok) {
            setKioskMode("unlocked");
            return;
          }
        } catch {
          localStorage.removeItem(KIOSK_SESSION_KEY);
        }
      }

      // No valid URL/localStorage session - try to auto-create one if admin is authenticated
      try {
        const launchRes = await fetch(`/org/${orgSlug}/api/kiosk/admin-launch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        
        if (launchRes.ok) {
          const data = await launchRes.json();
          // Store new session
          const session: KioskSession = {
            sessionToken: data.sessionToken,
            expiresAt: data.expiresAt,
            orgSlug
          };
          localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
          
          // Update URL with session token for home screen bookmarking
          const newUrl = `/org/${orgSlug}/kiosk?session=${data.sessionToken}`;
          window.history.replaceState({}, '', newUrl);
          
          setKioskConfigured(true);
          setKioskMode("unlocked");
          return;
        }
        
        // Only redirect to login if not authenticated (401/403)
        // Other errors should fall through to PIN unlock mode
        if (launchRes.status === 401 || launchRes.status === 403) {
          window.location.href = "/";
          return;
        }
      } catch {
        // Network error - fall through to PIN mode
      }
      
      // Fall back to PIN unlock mode (kiosk is configured but no valid session)
      setKioskMode("locked");
    } catch (error) {
      console.error("Error checking kiosk session:", error);
      // On error, default to unlocked to not block users
      setKioskMode("unlocked");
    }
  };

  const handleAdminUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminPin || adminPin.length < 4 || !orgSlug) return;

    setIsAdminLoading(true);
    try {
      const response = await fetch(`/org/${orgSlug}/api/kiosk/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: adminPin }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const session: KioskSession = {
          sessionToken: data.sessionToken,
          expiresAt: data.expiresAt,
          orgSlug
        };
        localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
        setKioskMode("unlocked");
        setAdminPin("");
      } else {
        const error = await response.json();
        toast({
          title: "Incorrect PIN",
          description: error.message || "Please check the kiosk PIN and try again.",
          variant: "destructive"
        });
        setAdminPin("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to unlock kiosk. Please try again.",
        variant: "destructive"
      });
    }
    setIsAdminLoading(false);
  };

  const handleLockKiosk = async () => {
    const storedSession = localStorage.getItem(KIOSK_SESSION_KEY);
    if (storedSession && orgSlug) {
      try {
        const session: KioskSession = JSON.parse(storedSession);
        await fetch(`/org/${orgSlug}/api/kiosk/lock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionToken: session.sessionToken }),
          credentials: 'include'
        });
      } catch {
        // Ignore lock errors
      }
    }
    localStorage.removeItem(KIOSK_SESSION_KEY);
    setKioskMode("locked");
    resetToPhone();
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      let formatted = value;
      if (value.length > 6) {
        formatted = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
      } else if (value.length > 3) {
        formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
      }
      setPhone(formatted);
    }
  };

  const adoptAccount = (account: AccountInfo) => {
    setAccountInfo(account);

    // Track contact ID for authorized contact auth flow
    if (account.pinTarget?.type === 'contact') {
      setContactId(account.pinTarget.id);
    } else {
      setContactId(null);
    }
  };

  // DS-1: a first PIN needs a code texted to the phone on file. The server
  // refuses a first PIN for a group account outright — it goes through the
  // texted link instead.
  const stepAfterIdentity = (account: AccountInfo): KioskStep =>
    account.requiresPinSetup || !account.hasPin
      ? (account.isGroupParent ? "forgot-pin" : "verify-code")
      : "pin";

  const handlePhoneLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setIsLoading(true);
    try {
      const result = await lookupCustomer<AccountInfo>(phone.replace(/\D/g, ''));
      setChosenOption(null);
      if (result.kind === "choose") {
        setAccountOptions(result.options);
        setStep("pick-account");
      } else if (result.kind === "account") {
        adoptAccount(result.account);
        setStep("confirm-identity");
      } else if (result.kind === "rate-limited") {
        toast({
          title: "Too many attempts",
          description: "Please wait a while and try again.",
          variant: "destructive"
        });
      } else {
        setStep("create-account");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to look up account. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  // The buyer picked the account by name, so there is nothing left to
  // confirm: the server re-checks the choice against this phone and answers
  // with that one account.
  const handlePickAccount = async (option: LookupAccountOption) => {
    setIsLoading(true);
    try {
      const result = await lookupCustomer<AccountInfo>(phone.replace(/\D/g, ''), option);
      if (result.kind === "account") {
        setChosenOption(option);
        adoptAccount(result.account);
        setSetupCode("");
        setCodeStepError(null);
        setStep(stepAfterIdentity(result.account));
      } else {
        toast({
          title: result.kind === "rate-limited" ? "Too many attempts" : "Account not found",
          description: "Please enter your phone number again.",
          variant: "destructive"
        });
        resetToPhone();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to look up account. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handleConfirmIdentity = (isCorrect: boolean) => {
    if (isCorrect && accountInfo) {
      setSetupCode("");
      setCodeStepError(null);
      setStep(stepAfterIdentity(accountInfo));
    } else {
      resetToPhone();
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin || pin.length < 4 || !accountInfo) return;

    setIsLoading(true);
    try {
      const childAccountId = chosenChildAccountId(chosenOption);
      const response = await apiRequest("POST", `/api/customer/login`, {
        accountId: accountInfo.id,
        pin,
        ...(contactId !== null ? { contactId } : {}),
        ...(childAccountId !== undefined ? { childAccountId } : {}),
      });
      
      if (response.ok) {
        const { customUrl } = await response.json();
        // Pass kiosk=1 in URL for iOS PWA support
        const redirectPath = `/org/${orgSlug}/order/${customUrl}?kiosk=1`;
        setLocation(redirectPath);
      } else {
        const error = await response.json();
        toast({
          title: "Incorrect PIN",
          description: error.message || "Please check your PIN and try again.",
          variant: "destructive"
        });
        setPin("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to verify PIN. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPin || newPin.length < 4 || newPin !== confirmPin || isWeakPin(newPin) || !accountInfo) return;

    setIsLoading(true);
    try {
      const response = await apiRequest("POST", `/api/customer/set-pin`, {
        accountId: accountInfo.id,
        pin: newPin,
        // DS-1: a first-time PIN set must name the phone this account was
        // looked up by, and carry the code that was sent to it.
        phone: phone.replace(/\D/g, ''),
        code: setupCode,
        ...(contactId !== null ? { contactId } : {}),
      });
      
      if (response.ok) {
        const { customUrl } = await response.json();
        toast({
          title: "PIN created!",
          description: "Your account is now secured with a PIN.",
        });
        // Pass kiosk=1 in URL for iOS PWA support
        const redirectPath = `/org/${orgSlug}/order/${customUrl}?kiosk=1`;
        setLocation(redirectPath);
      } else {
        const error = await response.json();
        toast({
          title: "Error setting PIN",
          description: error.message || "Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      // apiRequest throws "<status>: <body>" on any non-2xx, so the server's
      // own message (wrong phone, too many attempts) has to be dug out here.
      const parsed = parseApiError(error);
      if (parsed.code && (PIN_CODE_ERROR_CODES as readonly string[]).includes(parsed.code)) {
        // The code went stale while the customer was choosing a PIN. Back to
        // the code step, which sends a fresh one; the PIN they typed is kept.
        setSetupCode("");
        setCodeStepError(parsed.message || "That code is no longer valid. We've sent you a new one.");
        setStep("verify-code");
      } else {
        toast({
          title: "Error",
          description: parsed.message || "Unable to set PIN. Please try again.",
          variant: "destructive"
        });
      }
    }
    setIsLoading(false);
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccountName.trim() || !phone.trim()) return;

    setIsLoading(true);
    try {
      const response = await apiRequest("POST", `/api/accounts/quick-register`, {
        name: newAccountName.trim(),
        phone: phone.replace(/\D/g, '')
      });
      
      if (response.ok) {
        const account: AccountInfo = await response.json();
        setAccountInfo(account);
        toast({
          title: "Account created!",
          description: `Welcome, ${account.name}! Now set up your PIN.`,
        });
        setSetupCode("");
        setCodeStepError(null);
        setStep("verify-code");
      } else {
        const error = await response.json();
        toast({
          title: "Error creating account",
          description: error.message || "Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to create account. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const resetToPhone = () => {
    setStep("phone");
    setPin("");
    setNewPin("");
    setConfirmPin("");
    setAccountInfo(null);
    setNewAccountName("");
    setContactId(null);
    setAccountOptions([]);
    setChosenOption(null);
    setSetupCode("");
    setCodeStepError(null);
  };

  const handleForgotPin = async () => {
    if (!accountInfo) return;
    
    setIsLoading(true);
    try {
      // This used to post to /api/accounts/:id/request-magic-link, a route
      // that does not exist, so the kiosk's reset link never sent.
      const response = await apiRequest("POST", `/api/customer/send-magic-link`, {
        accountId: accountInfo.id,
        method: "sms"
      });
      
      if (response.ok) {
        setStep("reset-sent");
      } else {
        const error = await response.json();
        toast({
          title: "Unable to send reset link",
          description: error.message || "Please try again or ask staff for help.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to send reset link. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  // Show loading state while checking kiosk session
  if (kioskMode === "checking") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-cn-brand-blue mb-4" />
            <p className="text-gray-500">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Kiosk mode is not switched on for this vendor
  if (kioskMode === "unavailable") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="card-kiosk-unavailable">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LockKeyhole className="h-10 w-10 text-gray-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Kiosk ordering isn't available
            </CardTitle>
            <CardDescription className="text-base">
              Kiosk mode isn't switched on for {vendorName}. You can still order from the regular sign-in page.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <Button
              className="w-full h-14 text-lg bg-cn-brand-blue hover:bg-cn-brand-blue/90"
              onClick={() => setLocation(`/org/${orgSlug}`)}
              data-testid="button-kiosk-go-to-sign-in"
            >
              Go to sign-in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show admin lock screen
  if (kioskMode === "locked") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0 bg-white">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-cn-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <LockKeyhole className="h-10 w-10 text-cn-brand-blue" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Kiosk Locked
            </CardTitle>
            <CardDescription className="text-base">
              Enter the staff PIN to unlock this kiosk
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleAdminUnlock} className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={4}
                  value={adminPin}
                  onChange={setAdminPin}
                  data-testid="input-admin-pin"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-14 h-14 text-2xl" />
                    <InputOTPSlot index={1} className="w-14 h-14 text-2xl" />
                    <InputOTPSlot index={2} className="w-14 h-14 text-2xl" />
                    <InputOTPSlot index={3} className="w-14 h-14 text-2xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 text-lg bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                disabled={adminPin.length < 4 || isAdminLoading}
                data-testid="button-unlock-kiosk"
              >
                {isAdminLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Unlocking...
                  </>
                ) : (
                  <>
                    <Unlock className="h-5 w-5 mr-2" />
                    Unlock Kiosk
                  </>
                )}
              </Button>
            </form>
            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-400 flex items-center justify-center gap-1">
                <Shield className="h-4 w-4" />
                Staff access only
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Lock button for staff - only show if kiosk is configured */}
      {kioskConfigured && (
        <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLockKiosk}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            data-testid="button-lock-kiosk"
          >
            <LockKeyhole className="h-4 w-4 mr-1" />
            Lock
          </Button>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-gray-900">
              {step === "phone" && "Welcome"}
              {step === "pick-account" && "Choose Your Account"}
              {step === "confirm-identity" && "Confirm Your Account"}
              {step === "pin" && "Enter Your PIN"}
              {step === "verify-code" && "Confirm Your Phone"}
              {step === "set-pin" && "Create Your PIN"}
              {step === "create-account" && "New Customer"}
              {step === "forgot-pin" && "Forgot Your PIN?"}
              {step === "reset-sent" && "Check Your Phone"}
            </CardTitle>
            <CardDescription className="text-base">
              {step === "phone" && "Enter your phone number to get started"}
              {step === "pick-account" && "Which account are you ordering for?"}
              {step === "confirm-identity" && "Is this your account?"}
              {step === "pin" && `Welcome back, ${accountInfo?.name}`}
              {step === "verify-code" && "Enter the code we text you, then create your PIN"}
              {step === "set-pin" && "Secure your account with a 4-digit PIN"}
              {step === "create-account" && "Let's set up your account"}
              {step === "forgot-pin" && "We'll text you a link to reset your PIN"}
              {step === "reset-sent" && "We sent a reset link to your phone"}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {step === "phone" && (
            <form onSubmit={handlePhoneLookup} className="space-y-6">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="tel"
                  placeholder="555-555-5555"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="text-xl h-14 pl-12 bg-gray-50 text-center font-mono tracking-wider"
                  maxLength={12}
                  autoFocus
                  data-testid="input-phone"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 text-lg bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                disabled={phone.replace(/\D/g, '').length < 10 || isLoading}
                data-testid="button-lookup"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </form>
          )}

          {step === "pick-account" && (
            <AccountPickerStep
              options={accountOptions}
              onPick={handlePickAccount}
              onBack={resetToPhone}
              isLoading={isLoading}
              buttonClassName="w-full h-14 text-left justify-start text-lg font-medium"
            />
          )}

          {step === "confirm-identity" && accountInfo && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <CheckCircle className="h-12 w-12 text-cn-brand-blue mx-auto mb-3" />
                <p className="text-2xl font-bold text-gray-900">{accountInfo.name}</p>
                <p className="text-gray-500 mt-1">{phone}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline"
                  className="h-14 text-lg"
                  onClick={() => handleConfirmIdentity(false)}
                  data-testid="button-not-me"
                >
                  Not Me
                </Button>
                <Button 
                  className="h-14 text-lg bg-cn-brand-green hover:bg-cn-brand-green/90"
                  onClick={() => handleConfirmIdentity(true)}
                  data-testid="button-yes-thats-me"
                >
                  Yes, That's Me
                </Button>
              </div>
              <button
                type="button"
                onClick={resetToPhone}
                className="w-full text-sm text-gray-500 hover:underline flex items-center justify-center"
                data-testid="link-different-number"
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Use a different phone number
              </button>
            </div>
          )}

          {step === "pin" && accountInfo && (
            <form onSubmit={handlePinSubmit} className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={4}
                  value={pin}
                  onChange={setPin}
                  data-testid="input-pin"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-14 h-14 text-2xl" />
                    <InputOTPSlot index={1} className="w-14 h-14 text-2xl" />
                    <InputOTPSlot index={2} className="w-14 h-14 text-2xl" />
                    <InputOTPSlot index={3} className="w-14 h-14 text-2xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 text-lg bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                disabled={pin.length < 4 || isLoading}
                data-testid="button-verify-pin"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5 mr-2" />
                    Continue
                  </>
                )}
              </Button>
              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setStep("forgot-pin")}
                  className="text-sm text-cn-brand-blue hover:underline"
                  data-testid="link-forgot-pin"
                >
                  Forgot your PIN?
                </button>
                <br />
                <button
                  type="button"
                  onClick={resetToPhone}
                  className="text-sm text-gray-500 hover:underline inline-flex items-center"
                  data-testid="link-different-number"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Use a different phone number
                </button>
              </div>
            </form>
          )}

          {step === "forgot-pin" && accountInfo && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <MessageSquare className="h-12 w-12 text-cn-brand-blue mx-auto mb-3" />
                <p className="text-gray-700">
                  We'll send a reset link to<br />
                  <strong>{phone}</strong>
                </p>
              </div>
              <Button 
                className="w-full h-14 text-lg bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                onClick={handleForgotPin}
                disabled={isLoading}
                data-testid="button-send-reset"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-5 w-5 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>
              <button
                type="button"
                onClick={() => setStep("pin")}
                className="w-full text-sm text-gray-500 hover:underline flex items-center justify-center"
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Back to PIN entry
              </button>
            </div>
          )}

          {step === "reset-sent" && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-6 text-center">
                <CheckCircle className="h-12 w-12 text-cn-brand-green mx-auto mb-3" />
                <p className="text-gray-700">
                  Check your phone for a text message with a link to reset your PIN.
                </p>
              </div>
              <Button 
                variant="outline"
                className="w-full h-14 text-lg"
                onClick={resetToPhone}
                data-testid="button-start-over"
              >
                Start Over
              </Button>
            </div>
          )}

          {step === "verify-code" && accountInfo && (
            <PinSetupCodeStep
              accountId={accountInfo.id}
              contactId={contactId}
              phone={phone}
              vendorName={vendorName}
              // A kiosk is a shared device: nobody opens their email on it.
              canEmailLink={false}
              onEmailLink={() => {}}
              onVerified={(code) => {
                setSetupCode(code);
                setCodeStepError(null);
                setStep("set-pin");
              }}
              onBack={resetToPhone}
              backLabel="Start over"
              initialError={codeStepError}
              size="kiosk"
              disabled={isLoading}
            />
          )}

          {step === "set-pin" && accountInfo && (
            <form onSubmit={handleSetPin} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Create PIN</label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={4}
                      value={newPin}
                      onChange={setNewPin}
                      data-testid="input-new-pin"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="w-14 h-14 text-2xl" />
                        <InputOTPSlot index={1} className="w-14 h-14 text-2xl" />
                        <InputOTPSlot index={2} className="w-14 h-14 text-2xl" />
                        <InputOTPSlot index={3} className="w-14 h-14 text-2xl" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Confirm PIN</label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={4}
                      value={confirmPin}
                      onChange={setConfirmPin}
                      data-testid="input-confirm-pin"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="w-14 h-14 text-2xl" />
                        <InputOTPSlot index={1} className="w-14 h-14 text-2xl" />
                        <InputOTPSlot index={2} className="w-14 h-14 text-2xl" />
                        <InputOTPSlot index={3} className="w-14 h-14 text-2xl" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {confirmPin.length === 4 && newPin !== confirmPin && (
                    <p className="text-red-500 text-sm text-center mt-2">PINs don't match</p>
                  )}
                  {newPin.length >= 4 && isWeakPin(newPin) && (
                    <p className="text-red-500 text-sm text-center mt-2" data-testid="text-weak-pin">{WEAK_PIN_MESSAGE}</p>
                  )}
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 text-lg bg-cn-brand-green hover:bg-cn-brand-green/90"
                disabled={newPin.length < 4 || newPin !== confirmPin || isWeakPin(newPin) || isLoading}
                data-testid="button-set-pin"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-5 w-5 mr-2" />
                    Create PIN & Continue
                  </>
                )}
              </Button>
              <button
                type="button"
                onClick={resetToPhone}
                className="w-full text-sm text-gray-500 hover:underline flex items-center justify-center"
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Start over
              </button>
            </form>
          )}

          {step === "create-account" && (
            <form onSubmit={handleCreateAccount} className="space-y-6">
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <p className="text-amber-800">
                  No account found for <strong>{phone}</strong>
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name / Business Name</label>
                  <Input
                    type="text"
                    placeholder="Enter your name"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    className="h-12 text-lg"
                    autoFocus
                    data-testid="input-new-account-name"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 text-lg bg-cn-brand-green hover:bg-cn-brand-green/90"
                disabled={!newAccountName.trim() || isLoading}
                data-testid="button-create-account"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-5 w-5 mr-2" />
                    Create Account
                  </>
                )}
              </Button>
              <button
                type="button"
                onClick={resetToPhone}
                className="w-full text-sm text-gray-500 hover:underline flex items-center justify-center"
                data-testid="link-different-number"
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Use a different phone number
              </button>
            </form>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
